import { getJson } from './api';

export type BotPermission = 'everyone' | 'moderator' | 'broadcaster';
export type BotInternalState =
  | 'native'
  | 'keep_simple'
  | 'rewrite_native'
  | 'retire_review'
  | 'already_replaced'
  | 'legacy_builtin_review'
  | 'review_automation';

export type BotCommandAction =
  | { type: 'play_sound' | 'random_list' | 'append_file'; value: string }
  | { type: 'random_sound'; values: string[] };

export type BotCommand = {
  command: string;
  enabled: boolean;
  cooldownSeconds: number;
  permission: BotPermission;
  lastTriggeredAt: string | null;
  description: string | null;
  handlerType: string;
  responseTemplate: string | null;
  aliases: string[];
  actions: BotCommandAction[];
  internalState: BotInternalState;
  source: string;
  ready: boolean;
  readinessReasons: string[];
};

export type BotKeywordAction =
  | { type: 'play_sound'; value: string }
  | { type: 'random_sound'; values: string[] };

export type BotKeyword = {
  keyword: string;
  response: string;
  enabled: boolean;
  isRegex: boolean;
  caseSensitive: boolean;
  cooldownSeconds: number;
  actions: BotKeywordAction[];
};

export type BotNotice = {
  legacySlot: string;
  name: string;
  enabled: boolean;
  offlineEnabled: boolean;
  intervalMin: number;
  intervalMax: number;
  requiredMessages: number;
  shuffle: boolean;
  messages: string[];
  disabledMessages: boolean[];
  lastSentAt: string | null;
  nextDueAt: string | null;
  messagesSinceLast: number;
};

export type BotGreeting = {
  userLogin: string;
  message: string;
  enabled: boolean;
};

export type TwitchBotSnapshot = {
  configured: boolean;
  enabled: boolean;
  connected: boolean;
  botId: string | null;
  botLogin: string | null;
  botName: string | null;
  broadcasterId: string | null;
  broadcasterName: string | null;
  broadcasterBotScope: boolean;
  scopesReady: boolean;
  lastEventAt: string | null;
  lastCommandAt: string | null;
  lastError: string | null;
  commands: BotCommand[];
  automation: {
    settings: {
      keywords_enabled?: boolean;
      greetings_enabled?: boolean;
      notices_enabled?: boolean;
      [key: string]: unknown;
    };
    keywords: BotKeyword[];
    notices: BotNotice[];
    greetings: BotGreeting[];
  };
  recentChat: Array<{
    id: string;
    userId: string | null;
    userName: string;
    userLogin: string;
    text: string;
    sentAt: string;
  }>;
  recent: Array<{
    command: string;
    userName: string | null;
    result: string;
    detail: string | null;
    createdAt: string;
  }>;
};

export type SoundChoice = { name: string; enabled: boolean };

type RawBotCommand = Omit<BotCommand, 'internalState'> & { migrationStatus: BotInternalState };
type RawTwitchBotSnapshot = Omit<TwitchBotSnapshot, 'commands'> & { commands: RawBotCommand[] };

function normaliseBot(raw: RawTwitchBotSnapshot): TwitchBotSnapshot {
  return {
    ...raw,
    commands: raw.commands.map(({ migrationStatus, ...command }) => ({
      ...command,
      internalState: migrationStatus,
    })),
  };
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(payload.message ?? `Request failed with HTTP ${response.status}.`);
  }
  return (await response.json().catch(() => ({}))) as T;
}

async function requestBot(path: string, init: RequestInit): Promise<TwitchBotSnapshot> {
  return normaliseBot(await request<RawTwitchBotSnapshot>(path, init));
}

export async function loadBot(): Promise<TwitchBotSnapshot> {
  return normaliseBot(await getJson<RawTwitchBotSnapshot>('/api/v1/twitch-bot'));
}

export async function loadBotWithSounds(): Promise<{ bot: TwitchBotSnapshot; sounds: SoundChoice[] }> {
  const [bot, soundboard] = await Promise.all([
    loadBot(),
    getJson<{ sounds?: Array<{ name?: string; enabled?: boolean }> }>('/api/v1/soundboard'),
  ]);
  const sounds = (soundboard.sounds ?? [])
    .filter((sound): sound is { name: string; enabled?: boolean } => typeof sound.name === 'string')
    .map((sound) => ({ name: sound.name, enabled: sound.enabled !== false }))
    .sort((left, right) => left.name.localeCompare(right.name, 'en-GB', { numeric: true }));
  return { bot, sounds };
}

export function setBotEnabled(enabled: boolean): Promise<TwitchBotSnapshot> {
  return requestBot('/api/v1/twitch-bot/settings', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export function disconnectBot(): Promise<TwitchBotSnapshot> {
  return requestBot('/api/v1/integrations/twitch/bot/disconnect', { method: 'POST' });
}

export function sendBotTestMessage(): Promise<{ ok: boolean }> {
  return request('/api/v1/twitch-bot/test-message', { method: 'POST' });
}

export function createBotCommand(input: {
  command: string;
  enabled: boolean;
  responseTemplate: string | null;
  soundName: string | null;
  cooldownSeconds: number;
  permission: BotPermission;
}): Promise<TwitchBotSnapshot> {
  return requestBot('/api/v1/twitch-bot/commands', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateBotCommand(
  command: string,
  changes: {
    enabled?: boolean;
    cooldownSeconds?: number;
    permission?: BotPermission;
    responseTemplate?: string | null;
    aliases?: string[];
    internalState?: BotInternalState;
  },
): Promise<TwitchBotSnapshot> {
  const { internalState, ...visibleChanges } = changes;
  return requestBot(`/api/v1/twitch-bot/commands/${encodeURIComponent(command)}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...visibleChanges,
      ...(internalState === undefined ? {} : { migrationStatus: internalState }),
    }),
  });
}

export function archiveBotCommand(command: string): Promise<TwitchBotSnapshot> {
  return updateBotCommand(command, { enabled: false, internalState: 'retire_review' });
}

export function restoreBotCommandForSetup(command: string): Promise<TwitchBotSnapshot> {
  return updateBotCommand(command, { internalState: 'rewrite_native' });
}

export function deleteBotCommand(command: string): Promise<TwitchBotSnapshot> {
  return requestBot(`/api/v1/twitch-bot/commands/${encodeURIComponent(command)}`, { method: 'DELETE' });
}

export function updateBotKeyword(
  keyword: string,
  changes: { enabled?: boolean; response?: string; cooldownSeconds?: number },
): Promise<TwitchBotSnapshot> {
  return requestBot(`/api/v1/twitch-bot/keywords/${encodeURIComponent(keyword)}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
}

export function updateBotNotice(
  legacySlot: string,
  changes: {
    name?: string;
    enabled?: boolean;
    offlineEnabled?: boolean;
    intervalMin?: number;
    intervalMax?: number;
    requiredMessages?: number;
    shuffle?: boolean;
    messages?: string[];
    disabledMessages?: boolean[];
  },
): Promise<TwitchBotSnapshot> {
  return requestBot(`/api/v1/twitch-bot/notices/${encodeURIComponent(legacySlot)}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
}

export function updateAutomationSettings(changes: {
  keywordsEnabled?: boolean;
  greetingsEnabled?: boolean;
  noticesEnabled?: boolean;
}): Promise<TwitchBotSnapshot> {
  return requestBot('/api/v1/twitch-bot/automation-settings', {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
}
